import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { api } from "../../api/client";
import { btnPrimary, type as text, theme } from "../../theme";
import { SheetCard, StatusTag, type StatusTone } from "../../components/Drawing";
import { LoadingBlock } from "../../components/Spinner";
import { NextSteps } from "../../components/NextSteps";
import { EntityTagsRow } from "../../components/EntityTags";
import { useDeepLinkScroll } from "../../hooks/useDeepLinkScroll";
import { EmptyState, Field, ModalHeader, ModalOverlay, input, textarea, secondaryBtn, dangerBtn, toDateInput, dateInputToIso, fmtDateOnly, todayInput } from "../resilience/shared";
import { AttentionList, FrameworkPick, Meta, OwnerField, REVIEW_INTERVALS, attentionFor, msRef, personName, standardsOf, useMembers, useMsEnrolment, useMsPermissions, words, type Person } from "./shared";
import { attempt, useClearErrorOnDialog, useMsOverview, useRegister } from "./useRegister";

interface Obligation {
  id: string; displayId: number | null; frameworkIds: string[]; title: string; source: string; reference: string | null; requirement: string | null;
  appliesTo: string | null; ownerUserId: string | null; owner: Person | null; ownerName: string | null; status: string; evaluationIntervalMonths: number;
  lastEvaluatedAt: string | null; lastResult: string | null; evaluationNote: string | null; nextEvaluationAt: string | null;
  standing: { state: string; nextEvaluationAt: string | null; overdue: boolean };
}
interface Vocabulary { sources: string[]; results: string[] }

const STANDING: Record<string, { tone: StatusTone; label: string }> = {
  compliant: { tone: "met", label: "Compliant" },
  partially_compliant: { tone: "partial", label: "Partially compliant" },
  non_compliant: { tone: "gap", label: "Not compliant" },
  not_evaluated: { tone: "none", label: "Never evaluated" },
};

type Form = {
  frameworkIds: string[]; title: string; source: string; reference: string; requirement: string; appliesTo: string; ownerUserId: string; ownerName: string;
  status: string; evaluationIntervalMonths: number; nextEvaluationAt: string;
};
const blank: Form = { frameworkIds: [], title: "", source: "legislation", reference: "", requirement: "", appliesTo: "", ownerUserId: "", ownerName: "", status: "active", evaluationIntervalMonths: 12, nextEvaluationAt: "" };

export function ObligationsRegister() {
  const { data, loading, error, reload } = useRegister<{ obligations: Obligation[]; vocabulary: Vocabulary }>("/management-system/obligations");
  const overview = useMsOverview();
  const enrolled = useMsEnrolment().filter((f) => f === "iso14001" || f === "iso45001");
  const members = useMembers();
  const { canWrite } = useMsPermissions();
  const [params] = useSearchParams();
  const [editing, setEditing] = useState<{ id: string | null; form: Form } | null>(null);
  const [evaluating, setEvaluating] = useState<{ o: Obligation; evaluatedAt: string; result: string; note: string } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  useDeepLinkScroll(params.get("obligation"), !!data && !overview.loading);
  useClearErrorOnDialog(setFormError, !!editing, !!evaluating);

  if (loading) return <LoadingBlock />;
  if (error || !data) return <p style={{ ...text.body, color: theme.color.dangerText }}>{error}</p>;
  const refresh = () => Promise.all([reload(), overview.reload()]);

  const save = async () => {
    if (!editing) return;
    const f = editing.form;
    const body = { ...f, reference: f.reference || null, requirement: f.requirement || null, appliesTo: f.appliesTo || null, ownerUserId: f.ownerUserId || null, ownerName: f.ownerName || null, nextEvaluationAt: dateInputToIso(f.nextEvaluationAt) };
    if (await attempt(() => (editing.id ? api.put(`/management-system/obligations/${editing.id}`, body) : api.post("/management-system/obligations", body)), setFormError, "Could not save the obligation")) {
      setEditing(null);
      await refresh();
    }
  };
  const evaluate = async () => {
    if (!evaluating) return;
    const body = { evaluatedAt: dateInputToIso(evaluating.evaluatedAt), result: evaluating.result, note: evaluating.note || null };
    if (await attempt(() => api.post(`/management-system/obligations/${evaluating.o.id}/evaluate`, body), setFormError, "Could not record the evaluation")) {
      setEvaluating(null);
      await refresh();
    }
  };
  const remove = async (o: Obligation) => {
    if (!window.confirm(`Delete ${msRef("obligation", o.displayId)} ${o.title}?`)) return;
    if (await attempt(() => api.del(`/management-system/obligations/${o.id}`), setFormError, "Could not delete the obligation")) await refresh();
  };

  const f = editing?.form;
  const set = (patch: Partial<Form>) => editing && setEditing({ ...editing, form: { ...editing.form, ...patch } });

  return (
    <>
      <AttentionList items={attentionFor(overview.data?.attention ?? [], "ms_obligation")} />
      {formError && !editing && !evaluating && <p style={{ ...text.body, color: theme.color.dangerText }}>{formError}</p>}
      <SheetCard label="Compliance obligations" reference="6.1.3 / 9.1.2" actions={canWrite ? <button type="button" style={secondaryBtn} onClick={() => setEditing({ id: null, form: blank })}><Plus size={12} /> Obligation</button> : undefined}>
        {data.obligations.length === 0 ? (
          <EmptyState
            title="No compliance obligations recorded"
            detail="List the legal requirements, permits, licences, contracts and voluntary commitments that apply to your environmental aspects or OH&S hazards, then evaluate compliance with each at the interval you set."
            action={canWrite ? <button type="button" style={btnPrimary} onClick={() => setEditing({ id: null, form: blank })}>Add an obligation</button> : undefined}
          />
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {data.obligations.map((o) => {
              const st = STANDING[o.standing.state] ?? STANDING.not_evaluated;
              return (
                <div key={o.id} data-deeplink={o.id} style={{ border: "1px solid var(--c-border)", borderRadius: 3, padding: "10px 12px", background: "var(--c-panel)", opacity: o.status === "active" ? 1 : 0.6 }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                    <span style={{ ...text.ref, color: theme.color.ref }}>{msRef("obligation", o.displayId)}</span>
                    <span style={text.itemTitle}>{o.title}</span>
                    <StatusTag tone={st.tone}>{st.label}</StatusTag>
                    {o.standing.overdue && o.status === "active" && <StatusTag tone="gap">Evaluation due</StatusTag>}
                    {canWrite && (
                      <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                        <button type="button" style={secondaryBtn} onClick={() => setEvaluating({ o, evaluatedAt: todayInput(), result: "compliant", note: "" })}>Evaluate</button>
                        <button type="button" style={secondaryBtn} onClick={() => setEditing({ id: o.id, form: { frameworkIds: o.frameworkIds, title: o.title, source: o.source, reference: o.reference ?? "", requirement: o.requirement ?? "", appliesTo: o.appliesTo ?? "", ownerUserId: o.ownerUserId ?? "", ownerName: o.ownerName ?? "", status: o.status, evaluationIntervalMonths: o.evaluationIntervalMonths, nextEvaluationAt: toDateInput(o.nextEvaluationAt) } })}>Edit</button>
                        <button type="button" style={dangerBtn} onClick={() => void remove(o)}>Delete</button>
                      </span>
                    )}
                  </div>
                  {o.requirement && <p style={{ ...text.body, margin: "4px 0", whiteSpace: "pre-line" }}>{o.requirement}</p>}
                  <Meta>
                    <span>Source: {words(o.source)}{o.reference ? ` · ${o.reference}` : ""}</span>
                    {o.appliesTo && <span>Applies to: {o.appliesTo}</span>}
                    <span>Last evaluated: {fmtDateOnly(o.lastEvaluatedAt)}</span>
                    <span>Next evaluation: {fmtDateOnly(o.standing.nextEvaluationAt)}</span>
                    <span>Owner: {personName(o.owner, o.ownerName)}</span>
                    <span>Standards: {standardsOf(o.frameworkIds, enrolled)}</span>
                  </Meta>
                  {o.evaluationNote && <div style={text.hint}>Evaluation: {o.evaluationNote}</div>}
                  <EntityTagsRow entity={{ kind: "ms_obligation", id: o.id }} readOnly={!canWrite} style={{ marginTop: 6 }} />
                </div>
              );
            })}
          </div>
        )}
      </SheetCard>

      {evaluating && (
        <ModalOverlay onClose={() => setEvaluating(null)}>
          <ModalHeader title={`Evaluate compliance: ${msRef("obligation", evaluating.o.displayId)}`} onClose={() => setEvaluating(null)} />
          <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {/* ISO 14001 9.1.2: the evaluation is retained with what it rested on. */}
            <div style={{ gridColumn: "1 / -1" }}>
              <NextSteps items={[
                { done: !!evaluating.evaluatedAt, label: "Give the date of the evaluation" },
                ...(evaluating.result !== "compliant" ? [{ done: !!evaluating.note.trim(), label: "Say what was checked and what was found", hint: "Required unless fully compliant.", urgent: true }] : []),
              ]} />
            </div>
            <Field label="Evaluated on"><input type="date" style={input} max={todayInput()} value={evaluating.evaluatedAt} onChange={(e) => setEvaluating({ ...evaluating, evaluatedAt: e.target.value })} /></Field>
            <Field label="Result">
              <select style={input} value={evaluating.result} onChange={(e) => setEvaluating({ ...evaluating, result: e.target.value })}>{data.vocabulary.results.map((r) => <option key={r} value={r}>{words(r)}</option>)}</select>
            </Field>
            <Field label="What it rested on" span2 help="Required unless fully compliant: what was checked and what was found."><textarea style={textarea} rows={3} value={evaluating.note} onChange={(e) => setEvaluating({ ...evaluating, note: e.target.value })} /></Field>
            {formError && <p style={{ gridColumn: "1 / -1", margin: 0, ...text.body, color: theme.color.dangerText }}>{formError}</p>}
            <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" style={secondaryBtn} onClick={() => setEvaluating(null)}>Cancel</button>
              <button type="button" style={btnPrimary} onClick={() => void evaluate()}>Record</button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {editing && f && (
        <ModalOverlay onClose={() => setEditing(null)}>
          <ModalHeader title={editing.id ? "Edit obligation" : "Add compliance obligation"} onClose={() => setEditing(null)} />
          <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {/* Ticks as the form is filled in: what ISO 14001 6.1.3 / ISO 45001 6.1.3 expect of each obligation. */}
            <div style={{ gridColumn: "1 / -1" }}>
              <NextSteps items={[
                { done: !!f.title.trim(), label: "Name the obligation" },
                { done: !!f.reference.trim(), label: "Give its reference", hint: "The act, permit or contract it comes from." },
                { done: !!f.requirement.trim(), label: "Say what it requires" },
                { done: !!f.appliesTo.trim(), label: "Say what it applies to", hint: "ISO 14001 6.1.3: how the obligation applies to the organisation." },
                { done: !!(f.ownerUserId || f.ownerName.trim()), label: "Name an owner" },
              ]} />
            </div>
            <Field label="Obligation" span2><input style={input} maxLength={300} value={f.title} onChange={(e) => set({ title: e.target.value })} /></Field>
            <Field label="Source">
              <select style={input} value={f.source} onChange={(e) => set({ source: e.target.value })}>{data.vocabulary.sources.map((s) => <option key={s} value={s}>{words(s)}</option>)}</select>
            </Field>
            <Field label="Reference"><input style={input} value={f.reference} onChange={(e) => set({ reference: e.target.value })} placeholder="Act, permit or contract number" /></Field>
            <Field label="What it requires" span2><textarea style={textarea} rows={3} value={f.requirement} onChange={(e) => set({ requirement: e.target.value })} /></Field>
            <Field label="Applies to" span2><input style={input} value={f.appliesTo} onChange={(e) => set({ appliesTo: e.target.value })} placeholder="Sites, activities, aspects or hazards" /></Field>
            <OwnerField userId={f.ownerUserId} name={f.ownerName} members={members} onChange={(o) => set({ ownerUserId: o.userId, ownerName: o.name })} />
            <Field label="Status">
              <select style={input} value={f.status} onChange={(e) => set({ status: e.target.value })}><option value="active">Active</option><option value="archived">Archived</option></select>
            </Field>
            <Field label="Evaluate every">
              <select style={input} value={f.evaluationIntervalMonths} onChange={(e) => set({ evaluationIntervalMonths: Number(e.target.value) })}>{REVIEW_INTERVALS.map((m) => <option key={m} value={m}>{m} months</option>)}</select>
            </Field>
            <Field label="Next evaluation" help="Left empty, one interval after the last evaluation. An obligation never evaluated is due one interval after it was recorded."><input type="date" style={input} value={f.nextEvaluationAt} onChange={(e) => set({ nextEvaluationAt: e.target.value })} /></Field>
            <FrameworkPick allowed={enrolled} value={f.frameworkIds} onChange={(ids) => set({ frameworkIds: ids })} />
            {formError && <p style={{ gridColumn: "1 / -1", margin: 0, ...text.body, color: theme.color.dangerText }}>{formError}</p>}
            <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" style={secondaryBtn} onClick={() => setEditing(null)}>Cancel</button>
              <button type="button" style={btnPrimary} disabled={!f.title.trim()} onClick={() => void save()}>Save</button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </>
  );
}
